import React from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import { simpleDate } from 'lib/date';
import { ClientType } from 'models/Client';
import LocationEpisode from 'models/LocationEpisode';
import Profile from 'models/Profile';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

type Props = {
  profile: Profile;
  onClickPatient: (id: string) => void;
};

const columnHelper = createColumnHelper<LocationEpisode>();

export default function allPatientsTableColumns({ profile, onClickPatient }: Props) {
  let ownerColumn = {} as ReturnType<typeof columnHelper.accessor>;

  switch (profile?.clientType) {
    case ClientType.PHYSICIAN_GROUP:
      ownerColumn = columnHelper.accessor((row) => row.owner.name, {
        id: 'ownerName',
        header: 'Physician Group',
        size: 100,
      });
      break;
    case ClientType.PAYOR:
      ownerColumn = columnHelper.accessor((row) => row.owner.name, {
        id: 'ownerName',
        header: 'Payer',
        size: 100,
      });
      break;
    default:
      ownerColumn = columnHelper.accessor((row) => row.hospital.name, {
        id: 'hospitalName',
        header: 'Hospital',
        cell: (info) => info.getValue() || '—',
        size: 100,
      });
  }

  return [
    columnHelper.accessor((row) => row.patient.name, {
      id: 'patientName',
      header: 'Name',
      cell: (info) => (
        <Link
          fontWeight='500'
          onClick={() => onClickPatient(info.row.original.episodeId)}
          data-dd-action-name='Patient name'>
          {info.getValue()}
        </Link>
      ),
      size: 100,
    }),
    ownerColumn,
    columnHelper.accessor((row) => row.group.name, {
      id: 'rehabFacility',
      header: 'Location',
      size: 100,
    }),
    columnHelper.accessor((row) => row.physicianTeam.name, {
      id: 'physicianTeamName',
      header: 'Physician',
      cell: (info) => info.getValue() || '—',
      size: 100,
    }),
    columnHelper.accessor((row) => row.planTypeClassification?.name, {
      id: 'planTypeClassification',
      header: 'Plan Type',
      cell: (info) => info.getValue() || '—',
      size: 100,
    }),
    columnHelper.accessor((row) => row.episodeClassifications.map((ec) => ec.name).join(', '), {
      id: 'episodeClassifications',
      header: 'Episode Type',
      cell: (info) => info.getValue() || '—',
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.admittedOn, {
      id: 'admittedOn',
      header: 'Hospital Admission',
      cell: (info) => simpleDate(info.getValue()) || '—',
      size: 100,
    }),
    columnHelper.accessor((row) => row.lengthOfStay, {
      id: 'lengthOfStay',
      header: 'Days',
      cell: (info) => {
        const trackLos = info.row.original.trackLos;

        return trackLos ? info.getValue() : '—';
      },
      size: 100,
    }),
  ];
}

const Link = styled(BodySmall)`
  cursor: pointer;
  color: ${() => colors.blue100};
`;
